/* Back Hero USA — bh-product buy box (29/09/2026), port of the approved mockup's [data-pdp] script.
 * Offer mode: offer cards → unit rows, gift tiles (scratch reveal), nudge, save box, button + sticky label.
 * Default mode: option swatches / selects → variant, quantity stepper, price row, button + sticky label.
 * Add to cart = ONE /cart/add.js with every paid unit (per-unit variants) + the unlocked $0 gifts as child lines
 * (parent_id = the first paid variant of the same request — shopify-bundle-flow nested-gifts.md), then Horizon's drawer
 * is refreshed with the standard CartLinesUpdateEvent (sections in the answer) and opens.
 * Gifts sent = what the cart will earn minus what it already holds (window.BH.giftGuard.giftsForAdd), so a second
 * "Buy 1" never doubles the gifts (owner rule: the highest tier reached, not multiplied).
 * Gallery thumbs, the video tile, the image viewer and back-to-top live in assets/bh.js. */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const routes = () => window.Theme?.routes || { cart_url: '/cart', cart_add_url: '/cart/add' };
const jsonUrl = (u) => (u.endsWith('.js') ? u : `${u}.js`);
const scrollTop = () => Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, ($('.page-wrapper') || {}).scrollTop || 0);

function moneyFmt(format) {
  return (cents) => {
    const f = format || '${{amount}}';
    const amt = (Math.round(cents) / 100).toFixed(2);
    const comma = amt.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return f
      .replace(/\{\{\s*amount\s*\}\}/, comma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toLocaleString('en-US'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amt.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toLocaleString('de-DE'))
      .replace(/<[^>]+>/g, '')
      .replace(/&amp;/g, '&');
  };
}

function init(pdp) {
  if (pdp.dataset.bhReady) return;
  pdp.dataset.bhReady = '1';
  const wrap = pdp.closest('.bh-product-sec');
  let cfg;
  try {
    cfg = JSON.parse($('script[data-bh-config]', wrap).textContent);
  } catch {
    return;
  }
  const sid = cfg.section;
  const fmt = moneyFmt(cfg.money);
  const V = cfg.variants || [];
  const byId = new Map(V.map((v) => [v.id, v]));
  const defV = byId.get(cfg.variant) || V.find((v) => v.a) || V[0];
  const bar = document.querySelector(`[data-bh-pbar="${sid}"]`);
  const sticky = bar && $('[data-bh-sticky]', bar);
  const stickyLabel = sticky && $('[data-sticky-label]', sticky);
  const stickyBtn = sticky && $('[data-sticky-atc]', sticky);
  const atc = $('[data-atc]', pdp);
  const atcPrice = $('[data-atc-price]', pdp);
  const atcText = $('[data-atc-text]', pdp);
  const err = $('[data-atc-err]', pdp);
  const offer = pdp.dataset.mode === 'offer';
  const compareOf = (v) => (v.c > v.p ? v.c : v.p);
  const findVariant = (opts) => V.find((v) => v.o.every((x, i) => opts[i] === undefined || x === opts[i]));

  /* ---------- option pickers (unit rows, compact picker, default rows) ---------- */
  const readOpts = (scope) => {
    const out = [];
    for (const el of $$('[data-opt]', scope)) {
      const i = Number(el.dataset.opt);
      if (el.tagName === 'SELECT') out[i] = el.value;
      else if (el.classList.contains('is-on')) out[i] = el.dataset.val;
    }
    return out;
  };
  const setOpt = (scope, i, val) => {
    for (const el of $$(`[data-opt="${i}"]`, scope)) {
      if (el.tagName === 'SELECT') el.value = val;
      else {
        const on = el.dataset.val === val;
        el.classList.toggle('is-on', on);
        el.setAttribute('aria-checked', String(on));
      }
    }
    const nm = $(`[data-opt-name="${i}"]`, scope);
    if (nm) nm.textContent = val;
  };
  const showVariantImage = (v) => {
    if (!cfg.swatchImage || !v?.img) return;
    const main = $('[data-main]', pdp);
    if (!main) return;
    const vid = $('[data-main-video]', pdp);
    if (vid) {
      vid.pause();
      vid.hidden = true;
    }
    main.hidden = false;
    main.removeAttribute('srcset');
    main.src = v.img;
    const base = v.img.split('?')[0].replace(/_\d+x\d*(?=\.)/, '');
    $$('.bh-gallery__thumbs img', pdp).forEach((t) => t.classList.toggle('is-on', (t.dataset.full || '').split('?')[0].replace(/_\d+x\d*(?=\.)/, '') === base));
  };

  // colour galleries (20/10/2026): picking a colour that has its own photo set (sections/bh-product.liquid prints a
  // <template data-color-gallery> per colour from custom.gallery_<colour>) swaps the thumbs and the main photo; picking a
  // colour without one brings back the product media. The last colour picked in any unit row wins.
  const galThumbs = $('.bh-gallery__thumbs', pdp);
  const galDefault = galThumbs ? galThumbs.innerHTML : '';
  let galColor = null;
  const showColorGallery = (i, val) => {
    if (!galThumbs) return;
    const tpls = $$(`template[data-color-gallery][data-opt="${i}"]`, pdp);
    if (!tpls.length) return;
    const want = String(val || '').trim().toLowerCase();
    if (want === galColor) return;
    galColor = want;
    const tpl = tpls.find((t) => t.dataset.colorGallery === want);
    // start from the product media, then swap the first photos for the colour's own; a set shorter than the media
    // (e.g. only a first photo) keeps the rest of the product photos
    galThumbs.innerHTML = galDefault;
    if (tpl) {
      const own = [...tpl.content.querySelectorAll('img')];
      const base = $$('img', galThumbs);
      own.forEach((im, n) => {
        const copy = im.cloneNode(true);
        if (base[n]) base[n].replaceWith(copy);
        else galThumbs.appendChild(copy);
      });
    }
    const first = $('img', galThumbs);
    const main = $('[data-main]', pdp);
    if (!first || !main) return;
    $$('img', galThumbs).forEach((t) => t.classList.toggle('is-on', t === first));
    const vid = $('[data-main-video]', pdp);
    if (vid) {
      vid.pause();
      vid.hidden = true;
    }
    main.hidden = false;
    if (first.dataset.srcset) main.srcset = first.dataset.srcset;
    else main.removeAttribute('srcset');
    main.src = first.dataset.full || first.src;
    main.alt = first.alt;
  };

  let refresh = () => {};
  // clicks / changes on any picker in this buy box
  const onPick = (el) => {
    const scope = el.closest('.bh-unit, .bh-ins-pick, .bh-units') || pdp;
    const i = Number(el.dataset.opt);
    const val = el.tagName === 'SELECT' ? el.value : el.dataset.val;
    const pick = el.closest('[data-bh-pick]');
    if (pick) {
      // compact picker: every unit row follows
      setOpt(pick, i, val);
      $$('.bh-unit[data-unit]', pdp).forEach((row) => setOpt(row, i, val));
    } else setOpt(el.closest('.bh-unit') || scope, i, val);
    const opts = readOpts(el.closest('.bh-unit, [data-bh-pick]') || pdp);
    showVariantImage(findVariant(opts));
    showColorGallery(i, val);
    refresh();
  };
  pdp.addEventListener('click', (e) => {
    const sw = e.target.closest('.bh-swatch[data-opt]');
    if (sw) onPick(sw);
  });
  pdp.addEventListener('change', (e) => {
    const s = e.target.closest('select[data-opt]');
    if (s) onPick(s);
  });

  /* ---------- sticky bar (shows once the main button has scrolled above the screen) ---------- */
  const barCheck = () => {
    if (!bar || !atc) return;
    const r = atc.getBoundingClientRect();
    const early = !!sticky && sticky.hasAttribute('data-sticky-early') && r.top > window.innerHeight;
    const on = !!sticky && (r.bottom < 0 || early) && pdp.offsetParent !== null;
    if (sticky) sticky.classList.toggle('is-show', on);
    bar.classList.toggle('has-bar', on);
  };
  let raf = 0;
  document.addEventListener('scroll', () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(barCheck);
  }, { passive: true, capture: true });
  window.addEventListener('resize', barCheck);

  /* ---------- delivery estimate (business days from today, weekends skipped) ---------- */
  const eta = $('[data-eta]', pdp);
  const etaDates = eta && $('[data-eta-dates]', eta);
  if (etaDates) {
    const add = (n) => {
      const d = new Date();
      let k = 0;
      while (k < n) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) k++;
      }
      if (n === 0) while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    const a = add(Number(eta.dataset.min) || 0), b = add(Number(eta.dataset.max) || 0);
    etaDates.textContent = a === b ? b : `${a} – ${b}`;
  }

  /* ---------- "Delivered by" date inside the Add to cart button (owner 2/11/2026): today + N business days, shopper's time zone ---------- */
  for (const el of $$('[data-ship-date]', pdp)) {
    const d = new Date();
    let k = 0;
    const n = Number(el.dataset.days) || 0;
    while (k < n) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) k++;
    }
    const day = d.getDate();
    const suf = day % 100 >= 11 && day % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][day % 10] || 'th';
    el.textContent = `${d.toLocaleDateString('en-US', { month: 'short' })} ${day}${suf}`;
  }

  /* ---------- busy / error ---------- */
  const setBusy = (on) => {
    for (const b of [atc, stickyBtn]) {
      if (!b) continue;
      if (on) b.setAttribute('aria-busy', 'true');
      else b.removeAttribute('aria-busy');
    }
  };
  const showErr = (msg) => {
    if (!err) return;
    err.textContent = msg || '';
    err.hidden = !msg;
  };

  /** ONE /cart/add.js, then the standard event so Horizon's drawer repaints from the sections and opens */
  async function send(items, from, meta) {
    const deferred = CartLinesUpdateEvent.createPromise();
    (from || atc).dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
    const ids = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const guard = window.BH?.giftGuard;
    // the drawer AND the whole cart come back in the add answer (sections/bh-product-cart): one request, no /cart.js read
    const want = guard ? [...ids, guard.cartSection] : ids;
    try {
      const res = await fetch(jsonUrl(routes().cart_add_url), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, ...(want.length ? { sections: want.join(','), sections_url: location.pathname } : {}) }),
      });
      const data = await res.json();
      let cart = !data.status && guard ? guard.cartFromAnswer(data) : null;
      if (!cart) cart = await (await fetch(jsonUrl(routes().cart_url), { headers: { Accept: 'application/json' } })).json();
      if (data.sections && guard) delete data.sections[guard.cartSection];
      guard?.noteCart(cart);
      if (data.status) {
        (from || atc).dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID', detail: { description: data.description } }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'bh-product' } });
        showErr(data.description || data.message || 'Could not add to cart.');
        return false;
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'bh-product', sourceId: sid, itemCount: items.reduce((s, i) => s + i.quantity, 0), productId: meta?.productId, sections: data.sections, didError: false },
      });
      // open the drawer (Horizon only opens it itself when the theme setting "Auto-open cart drawer" is on); no drawer → cart page
      const cd = document.querySelector('cart-drawer-component');
      const td = cd?.closest('theme-drawer');
      if (!cd) location.href = routes().cart_url;
      else if (!cd.hasAttribute('auto-open') && td && !td.hasAttribute('open')) td.open?.();
      return true;
    } catch (e) {
      deferred.reject(e);
      showErr('Could not add to cart. Please try again.');
      return false;
    }
  }

  let busy = false;
  let addNow = async () => {};
  const onAdd = async (from) => {
    if (busy) return;
    busy = true;
    setBusy(true);
    showErr('');
    try {
      await addNow(from);
    } finally {
      busy = false;
      setBusy(false);
    }
  };
  if (atc) atc.addEventListener('click', () => onAdd(atc));
  if (stickyBtn) stickyBtn.addEventListener('click', () => onAdd(stickyBtn));

  if (offer) offerMode();
  else defaultMode();
  barCheck();

  /* ================= offer mode ================= */
  function offerMode() {
    const T = cfg.tiers || [];
    const G = cfg.gifts || [];
    const cards = $$('.bh-offer', pdp);
    const rows = $$('.bh-unit[data-unit]', pdp);
    const tiles = $$('.bh-gb', pdp);
    const pick = $('[data-bh-pick]', pdp);
    const mix = $('[data-bh-mix]', pdp);
    const nudge = $('[data-nudge]', pdp);
    const count = $('[data-gift-count]', pdp);
    const pips = $$('.bh-pips i', pdp);
    const sizeRows = $$('[data-gift-size]', pdp);
    let cur = Number((cards.find((c) => c.classList.contains('is-on')) || cards[0])?.dataset.tier || 1);
    let ready = false;
    const tier = () => T[cur - 1] || { qty: 1, pct: 0, free: 0, name: '', sticky: '' };
    const mixed = () => !!mix && !mix.hidden;

    const unitVariant = (i) => {
      if (!rows.length) return defV;
      const scope = pick && !mixed() ? pick : rows[i - 1] || rows[0];
      return findVariant(readOpts(scope)) || defV;
    };
    const isFree = (i, t) => {
      if (!t.free) return false;
      const step = Math.floor(t.qty / t.free);
      return step > 0 && i <= t.qty && i % step === 0;
    };
    const priceOf = (t) => {
      let sum = 0;
      let cmp = 0;
      let pctSum = 0;
      for (let i = 1; i <= t.qty; i++) {
        const v = unitVariant(i);
        sum += v.p;
        // Shopify takes a % discount per unit and rounds each unit's discount DOWN to the cent (live cart 30/09:
        // TF 2 × $99.95 at 5% = $189.92, posture 2 × $49.99 at 10% = $90.00), so the card must do the same
        // a fixed amount off each unit (t.off, cents) replaces the % when set (posture Buy 2: $5 off each = $89.98, owner 01/10)
        pctSum += v.p - (t.off > 0 ? Math.min(t.off, v.p) : Math.floor((v.p * t.pct) / 100));
        cmp += compareOf(v);
      }
      // free-unit tiers (Buy 1 Get 1) keep the plain maths: the free unit is exactly 100% off
      return { price: t.free ? Math.round((sum * (100 - t.pct)) / 100) : pctSum, compare: cmp };
    };
    const giftVariant = (gi) => {
      const sel = $(`[data-gift-variant="${gi}"]`, pdp);
      return Number(sel?.value) || G[gi]?.variant || 0;
    };

    function saveBox(t, price) {
      const box = $('[data-save]', pdp);
      if (!box) return;
      const list = $('[data-save-list]', box);
      let paidCmp = 0;
      let freeCmp = 0;
      let paidN = 0;
      for (let i = 1; i <= t.qty; i++) {
        const c = compareOf(unitVariant(i));
        if (isFree(i, t)) freeCmp += c;
        else {
          paidCmp += c;
          paidN++;
        }
      }
      const freeN = t.qty - paidN;
      const li = (name, val, free) => {
        const el = document.createElement('li');
        const a = document.createElement('span');
        a.textContent = name;
        const b = document.createElement('span');
        if (free) {
          b.className = 'free';
          const s = document.createElement('s');
          s.textContent = fmt(val);
          b.append(s, 'FREE');
        } else b.textContent = fmt(val);
        el.append(a, b);
        return el;
      };
      const out = [li(`${paidN}× ${cfg.short}`, paidCmp, false)];
      if (freeN > 0) out.push(li(`Free: ${t.freeLabel || (freeN === 1 ? `2nd ${cfg.short}` : `${freeN} more ${cfg.short}`)}`, freeCmp, true));
      let giftSum = 0;
      G.forEach((g) => {
        if (g.tier > cur) return;
        const val = g.value * g.count;
        giftSum += val;
        out.push(li(`Free gift: ${g.save || g.title}`, val, true));
      });
      list.replaceChildren(...out);
      const retail = paidCmp + freeCmp + giftSum;
      $('[data-save-retail]', box).textContent = fmt(retail);
      $('[data-save-pay]', box).textContent = fmt(price);
      $('[data-save-amt]', box).textContent = fmt(retail - price);
      box.hidden = retail - price <= 0;
    }

    function gifts(animate) {
      let open = 0;
      let total = 0;
      tiles.forEach((el) => {
        const g = G[Number(el.dataset.gift)] || {};
        const n = Number(el.dataset.count) || 1;
        const on = Number(el.dataset.tier) <= cur;
        total += n;
        if (on) open += n;
        const was = el._want ?? el.classList.contains('is-open');
        el._want = on;
        el.setAttribute('aria-label', on ? `${g.title || ''}, free with this offer` : `${g.title || ''}, locked. Choose ${T[Number(el.dataset.tier) - 1]?.name || 'a bigger offer'} to unlock`);
        if (on === was) return;
        clearTimeout(el._t);
        if (on && animate && !reduce()) {
          el.classList.add('is-shake');
          el._t = setTimeout(() => {
            el.classList.remove('is-shake');
            el.classList.add('is-open', 'is-pop');
            el._t = setTimeout(() => el.classList.remove('is-pop'), 800);
          }, 380);
        } else {
          el.classList.remove('is-shake', 'is-pop');
          el.classList.toggle('is-open', on);
        }
      });
      // all unlocked → said once in the gift heading (green), the nudge bar hides (owner 01/10: "gộp… cho nó gọn")
      const done = open >= total;
      if (count) {
        count.textContent = done ? `All ${total} unlocked` : `${open} of ${total} unlocked`;
        count.parentElement.classList.toggle('is-done', done);
      }
      pips.forEach((p, i) => p.classList.toggle('on', i < open));
      if (nudge) {
        nudge.classList.toggle('is-done', done);
        nudge.hidden = done;
        nudge.replaceChildren();
        if (!done) {
          const b = document.createElement('button');
          b.type = 'button';
          b.dataset.pickTop = '';
          b.textContent = nudge.dataset.label || T[T.length - 1]?.name || '';
          nudge.append('Pick ', b, total > 1 ? ` to unbox all ${total} gifts` : ' to unbox your free gift');
        }
      }
      sizeRows.forEach((r) => {
        const g = G[Number(r.dataset.giftSize)];
        r.hidden = !g || g.tier > cur;
      });
    }

    refresh = () => {
      const t = tier();
      const { price } = priceOf(t);
      rows.forEach((r) => {
        const i = Number(r.dataset.unit);
        r.hidden = i > t.qty;
        const f = $('[data-free]', r);
        if (f) f.hidden = !isFree(i, t);
      });
      $$('[data-ins-n]', pdp).forEach((n) => (n.textContent = t.qty));
      // every card follows the chosen colours / sizes (prices can differ per variant)
      cards.forEach((c, k) => {
        const tt = T[k];
        if (!tt) return;
        const pc = priceOf(tt);
        const p = $('[data-o-price]', c);
        if (p) p.textContent = fmt(pc.price);
        const s = $('[data-o-compare]', c);
        if (s) s.textContent = fmt(pc.compare);
        const e = $('[data-o-save]', c);
        if (e && pc.compare > 0) e.textContent = `Save ${Math.round(((pc.compare - pc.price) * 100) / pc.compare)}%`;
      });
      if (atcPrice) atcPrice.textContent = fmt(price);
      if (stickyLabel) stickyLabel.textContent = `${t.sticky || t.name} · ${fmt(price)}`;
      const anyOut = Array.from({ length: t.qty }, (_, i) => unitVariant(i + 1)).some((v) => !v.a);
      if (atc) {
        atc.disabled = anyOut;
        if (atcText) atcText.textContent = anyOut ? 'Sold out' : cfg.atc;
      }
      if (stickyBtn) stickyBtn.disabled = anyOut;
      saveBox(t, price);
      pdp.dataset.tier = String(cur);
    };

    function select(n, animate = ready) {
      if (!T[n - 1]) return;
      cur = n;
      cards.forEach((c) => {
        const on = Number(c.dataset.tier) === n;
        c.classList.toggle('is-on', on);
        c.setAttribute('aria-checked', String(on));
        c.tabIndex = on ? 0 : -1;
      });
      gifts(animate);
      refresh();
    }

    cards.forEach((c) => {
      c.addEventListener('click', () => select(Number(c.dataset.tier)));
      c.addEventListener('keydown', (e) => {
        const k = e.key;
        if (k === 'Enter' || k === ' ') {
          e.preventDefault();
          select(Number(c.dataset.tier));
        } else if (k === 'ArrowDown' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowLeft') {
          e.preventDefault();
          const d = k === 'ArrowDown' || k === 'ArrowRight' ? 1 : -1;
          const n = ((Number(c.dataset.tier) - 1 + d + cards.length) % cards.length) + 1;
          select(n);
          cards[n - 1].focus();
        }
      });
    });

    /* gift tiles: locked → pick the tier that unlocks it; open → nothing (the popup below is no longer opened, owner 30/09);
       nudge → best tier */
    const qv = document.querySelector(`[data-bh-qv="${sid}"]`);
    let qvFrom = null;
    const openGift = (el) => {
      if (!qv) return;
      const g = G[Number(el.dataset.gift)] || {};
      const im = $('.bh-qv__img img', qv);
      im.src = g.img || '';
      im.alt = g.title || '';
      $('h3', qv).textContent = g.title || '';
      $('.bh-qv__price s', qv).textContent = fmt((g.value || 0) * (g.count || 1));
      $('.bh-qv__desc', qv).textContent = g.desc || '';
      qvFrom = el;
      qv.hidden = false;
      $('.bh-qv__x', qv).focus();
    };
    const closeGift = () => {
      if (!qv || qv.hidden) return;
      qv.hidden = true;
      if (qvFrom) qvFrom.focus();
    };
    if (qv && !qv.dataset.bhReady) {
      qv.dataset.bhReady = '1';
      qv.addEventListener('click', (e) => {
        if (e.target === qv || e.target.closest('.bh-qv__x')) closeGift();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeGift();
      });
    }
    pdp.addEventListener('click', (e) => {
      const top = e.target.closest('[data-pick-top]');
      if (top) {
        select(Number(nudge?.dataset.top) || T.length);
        return;
      }
      const el = e.target.closest('.bh-gb');
      if (!el) return;
      // owner 30/09: tapping an unlocked gift does nothing — no info popup (as WakaPaw since 27/09); a locked one picks its tier
      if (!el.classList.contains('is-open')) select(Number(el.dataset.tier));
    });

    /* compact picker ↔ one row per unit */
    const mixBtn = $('[data-ins-mix]', pdp);
    const sameBtn = $('[data-ins-same]', pdp);
    if (mixBtn && mix && pick) {
      mixBtn.addEventListener('click', () => {
        pick.hidden = true;
        mix.hidden = false;
        refresh();
        (rows[0] && $('[data-opt]', rows[0]))?.focus();
      });
    }
    if (sameBtn && mix && pick) {
      sameBtn.addEventListener('click', () => {
        const first = readOpts(rows[0]);
        first.forEach((val, i) => {
          if (val === undefined) return;
          setOpt(pick, i, val);
          rows.forEach((r) => setOpt(r, i, val));
        });
        mix.hidden = true;
        pick.hidden = false;
        refresh();
        mixBtn?.focus();
      });
    }

    $$('[data-gift-variant]', pdp).forEach((s) => s.addEventListener('change', refresh));

    // ?offer=N pre-selects offer N before anything animates
    const want = Number(new URLSearchParams(location.search).get('offer'));
    pdp.classList.add('gb-init');
    select(want >= 1 && want <= T.length ? want : cur, false);
    void pdp.offsetWidth;
    requestAnimationFrame(() => requestAnimationFrame(() => pdp.classList.remove('gb-init')));
    ready = true;

    addNow = async (from) => {
      const t = tier();
      const paid = [];
      for (let i = 1; i <= t.qty; i++) {
        const v = unitVariant(i);
        const hit = paid.find((x) => x.id === v.id);
        if (hit) hit.quantity++;
        else paid.push({ id: v.id, quantity: 1 });
      }
      if (!paid.length) return;
      // gifts: exactly what the cart will earn minus what it already holds (guard); the page's own set without the guard
      const chosen = {};
      G.forEach((g, gi) => {
        if (g.tier <= cur && g.handle && !chosen[g.handle]) chosen[g.handle] = giftVariant(gi);
      });
      let gifts = [];
      const guard = window.BH?.giftGuard;
      if (guard) {
        try {
          gifts = await guard.giftsForAdd(cfg.handle, t.qty, (h) => chosen[h]);
        } catch {
          gifts = null;
        }
      }
      if (!guard || gifts === null) {
        gifts = [];
        G.forEach((g, gi) => {
          if (g.tier > cur) return;
          const id = giftVariant(gi);
          const hit = gifts.find((x) => x.id === id);
          if (hit) hit.quantity += g.count;
          else if (id) gifts.push({ id, quantity: g.count });
        });
      }
      const parent = paid[0].id;
      const items = [...paid, ...gifts.map((g) => ({ id: g.id, quantity: g.quantity, parent_id: parent }))];
      if (gifts.length) guard?.notePdpAdd();
      await send(items, from, { productId: cfg.productId });
    };
  }

  /* ================= default mode ================= */
  function defaultMode() {
    const input = $('[data-qty-input]', pdp);
    const minus = $('[data-qty-step="-1"]', pdp);
    const plus = $('[data-qty-step="1"]', pdp);
    const priceRow = $('[data-bh-price]', pdp);
    const max = cfg.maxQty || 10;
    const qty = () => Math.max(1, Math.min(max, Math.round(Number(input?.value)) || 1));
    const variant = () => (V.length > 1 ? findVariant(readOpts(pdp)) : defV) || defV;
    const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;');
    refresh = () => {
      const v = variant();
      const q = qty();
      if (input) input.value = q;
      if (minus) minus.disabled = q <= 1;
      if (plus) plus.disabled = q >= max;
      if (priceRow) {
        const save = v.c > v.p ? Math.round((1 - v.p / v.c) * 100) : 0;
        priceRow.innerHTML = `<strong>${esc(fmt(v.p))}</strong>${save ? `<s>${esc(fmt(v.c))}</s><span class="bh-save-tag">Save ${save}%</span>` : ''}`;
      }
      if (atcPrice) atcPrice.textContent = fmt(v.p * q);
      if (atc) {
        atc.disabled = !v.a;
        if (atcText) atcText.textContent = v.a ? cfg.atc : 'Sold out';
      }
      if (stickyBtn) stickyBtn.disabled = !v.a;
      const opts = V.length > 1 ? v.o : [];
      if (stickyLabel) stickyLabel.textContent = `${opts.length ? `${opts.join(' / ')} · ` : ''}${q > 1 ? `Qty ${q} · ` : ''}${fmt(v.p * q)}`;
    };
    pdp.addEventListener('click', (e) => {
      const b = e.target.closest('[data-qty-step]');
      if (!b || b.disabled || !input) return;
      input.value = qty() + Number(b.dataset.qtyStep);
      refresh();
    });
    input?.addEventListener('change', refresh);
    addNow = async (from) => {
      const v = variant();
      if (!v?.a) return;
      await send([{ id: v.id, quantity: qty() }], from, { productId: cfg.productId });
    };
    refresh();
  }
}

function initAll(root = document) {
  $$('[data-bh-pdp]', root).forEach(init);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
else initAll();
document.addEventListener('shopify:section:load', (e) => initAll(e.target));
